/**
 * QuestionnaireWizard - Main questionnaire component
 * 
 * Multi-step wizard that guides users through the comprehensive financial questionnaire
 */

import * as React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import {
  nextQuestion,
  previousQuestion,
  setAnswer,
  skipQuestion,
  goToSection,
  completeQuestionnaire,
  setSections,
} from '@/store'
import { questionnaireSections, estimatedTotalMinutes } from '@/data/questionnaireQuestions'
import type { Question, QuestionSection } from '@/types'
import { SECTION_ORDER } from '@/types/questionnaire.types'
import { validateAnswer } from '@/types/questionnaire.types'
import { QuestionCard } from './QuestionCard'
import { SectionProgress, SectionProgressVertical } from './SectionProgress'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { ArrowLeft, ArrowRight, SkipForward, Check, Sparkles, Clock, ClipboardList } from 'lucide-react'
import { cn } from '@/lib/utils'

interface QuestionnaireWizardProps {
  onComplete?: () => void
  showSidebar?: boolean
}

export function QuestionnaireWizard({ onComplete, showSidebar = true }: QuestionnaireWizardProps) {
  const dispatch = useAppDispatch()
  const { progress, sections, isCompleted } = useAppSelector((state) => state.questionnaire)
  const [error, setError] = React.useState<string>('')
  const [isStarted, setIsStarted] = React.useState(!!progress.startedAt)

  // Initialize sections on mount
  React.useEffect(() => {
    if (sections.length === 0) {
      dispatch(setSections(questionnaireSections))
    }
  }, [dispatch, sections.length])

  // Get current question
  const currentSection = React.useMemo(() => {
    const sectionData = questionnaireSections.find(s => s.id === progress.currentSection)
    return sectionData || questionnaireSections[0]
  }, [progress.currentSection])

  const currentQuestion = currentSection?.questions[progress.currentQuestionIndex]

  // Check if question should be shown based on conditions
  const shouldShowQuestion = React.useCallback((question: Question): boolean => {
    if (!question.showIf) return true
    
    return question.showIf.every(condition => {
      const answerValue = progress.answers[condition.questionId]
      
      switch (condition.operator) {
        case 'equals':
          return answerValue === condition.value
        case 'not_equals':
          return answerValue !== condition.value
        case 'includes':
          if (typeof condition.value === 'string') {
            const validValues = condition.value.split(',')
            return validValues.includes(String(answerValue))
          }
          return false
        case 'greater_than':
          return Number(answerValue) > Number(condition.value)
        case 'less_than':
          return Number(answerValue) < Number(condition.value)
        default:
          return true
      }
    })
  }, [progress.answers])

  // Calculate visible questions for progress
  const visibleQuestions = React.useMemo(() => {
    return currentSection?.questions.filter(shouldShowQuestion) || []
  }, [currentSection, shouldShowQuestion])

  const currentVisibleIndex = React.useMemo(() => {
    if (!currentQuestion) return 0
    return visibleQuestions.findIndex(q => q.id === currentQuestion.id) + 1
  }, [currentQuestion, visibleQuestions])

  // Calculate total progress
  const totalProgress = React.useMemo(() => {
    const totalQuestions = questionnaireSections.reduce((sum, s) => 
      sum + s.questions.filter(shouldShowQuestion).length, 0
    )
    const answeredQuestions = Object.keys(progress.answers).length + progress.skippedQuestions.length
    return Math.round((answeredQuestions / Math.max(totalQuestions, 1)) * 100)
  }, [progress.answers, progress.skippedQuestions, shouldShowQuestion])

  // Handle next
  const handleNext = React.useCallback(() => {
    if (!currentQuestion) return

    // Validate if required
    const validation = validateAnswer(currentQuestion, progress.answers[currentQuestion.id])
    if (!validation.isValid && currentQuestion.validation?.required) {
      setError(validation.errors[0] || 'Please answer this question')
      return
    }

    setError('')
    
    // Find next visible question
    let nextIndex = progress.currentQuestionIndex + 1
    while (nextIndex < currentSection.questions.length) {
      const nextQ = currentSection.questions[nextIndex]
      if (shouldShowQuestion(nextQ)) {
        dispatch(nextQuestion())
        return
      }
      nextIndex++
    }
    
    // Move to next section
    dispatch(nextQuestion())
  }, [currentQuestion, progress.answers, progress.currentQuestionIndex, currentSection, shouldShowQuestion, dispatch])

  // Handle previous
  const handlePrevious = React.useCallback(() => {
    setError('')
    
    // Find previous visible question
    let prevIndex = progress.currentQuestionIndex - 1
    while (prevIndex >= 0) {
      const prevQ = currentSection.questions[prevIndex]
      if (shouldShowQuestion(prevQ)) {
        dispatch(previousQuestion())
        return
      }
      prevIndex--
    }
    
    dispatch(previousQuestion())
  }, [progress.currentQuestionIndex, currentSection, shouldShowQuestion, dispatch])

  // Handle skip
  const handleSkip = React.useCallback(() => {
    if (!currentQuestion) return
    
    if (currentQuestion.validation?.required) {
      setError('This question cannot be skipped')
      return
    }
    
    dispatch(skipQuestion(currentQuestion.id))
    handleNext()
  }, [currentQuestion, dispatch, handleNext])

  // Handle answer change
  const handleAnswerChange = React.useCallback((value: unknown) => {
    if (!currentQuestion) return
    setError('')
    dispatch(setAnswer({ questionId: currentQuestion.id, value }))
  }, [currentQuestion, dispatch])

  // Handle section click
  const handleSectionClick = React.useCallback((section: QuestionSection) => {
    setError('')
    dispatch(goToSection(section))
  }, [dispatch])

  // Handle completion
  const handleComplete = React.useCallback(() => {
    dispatch(completeQuestionnaire())
    onComplete?.()
  }, [dispatch, onComplete])

  // Track if we're currently auto-skipping a hidden question
  const [isSkipping, setIsSkipping] = React.useState(false)

  // Auto-skip hidden questions (must be at top level, not conditional)
  React.useEffect(() => {
    if (!isStarted || isCompleted) return
    if (!currentQuestion) return
    
    if (!shouldShowQuestion(currentQuestion)) {
      setIsSkipping(true)
      // Use setTimeout to avoid state update during render
      const timeout = setTimeout(() => {
        // Find next visible question index within current section
        let nextIndex = progress.currentQuestionIndex + 1
        while (nextIndex < currentSection.questions.length) {
          const nextQ = currentSection.questions[nextIndex]
          if (shouldShowQuestion(nextQ)) {
            break
          }
          nextIndex++
        }
        dispatch(nextQuestion())
        setIsSkipping(false)
      }, 0)
      return () => clearTimeout(timeout)
    } else {
      setIsSkipping(false)
    }
  }, [currentQuestion?.id, progress.currentQuestionIndex, progress.currentSection, shouldShowQuestion, isStarted, isCompleted, currentSection.questions, dispatch])

  // If completed, show completion screen
  if (isCompleted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-2xl mx-auto text-center py-12"
      >
        <div className="mb-8">
          <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="h-10 w-10 text-primary" />
          </div>
          <h2 className="text-3xl font-bold mb-4">Questionnaire Complete!</h2>
          <p className="text-lg text-muted-foreground">
            Great job! We now have a comprehensive picture of your financial situation.
            Your personalized life plan is ready.
          </p>
        </div>
        <Button size="lg" onClick={onComplete}>
          View Your Life Plan
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </motion.div>
    )
  }

  // Start screen
  if (!isStarted) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto"
      >
        <Card>
          <CardHeader className="text-center pb-4">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <ClipboardList className="h-8 w-8 text-primary" />
            </div>
            <CardTitle className="text-2xl">Financial Questionnaire</CardTitle>
            <CardDescription className="text-base">
              Answer a few questions to help us build your personalized life financial plan
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Section overview */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {questionnaireSections.map((section, index) => (
                <div
                  key={section.id}
                  className="flex items-center gap-3 p-3 rounded-lg bg-muted/50"
                >
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary">
                    {index + 1}
                  </div>
                  <div>
                    <div className="font-medium text-sm">{section.title}</div>
                    <div className="text-xs text-muted-foreground">
                      {section.questions.length} questions
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Estimated time */}
            <div className="flex items-center justify-center gap-2 text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span className="text-sm">Estimated time: {estimatedTotalMinutes} minutes</span>
            </div>

            {/* Start button */}
            <div className="flex flex-col items-center gap-3 pt-4">
              <Button size="lg" onClick={() => setIsStarted(true)} className="w-full sm:w-auto">
                <Sparkles className="mr-2 h-5 w-5" />
                Start Questionnaire
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                You can skip questions and come back anytime
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    )
  }

  // While skipping hidden questions, show nothing to prevent flash
  if (isSkipping || (currentQuestion && !shouldShowQuestion(currentQuestion))) {
    return null
  }

  // Main wizard view
  return (
    <div className={cn('flex gap-6', showSidebar && 'lg:gap-8')}>
      {/* Sidebar progress (desktop) */}
      {showSidebar && (
        <aside className="hidden lg:block w-64 flex-shrink-0">
          <div className="sticky top-6 space-y-6">
            <div>
              <div className="text-sm font-medium text-muted-foreground mb-2">
                Overall Progress
              </div>
              <Progress value={totalProgress} className="h-2" />
              <div className="text-xs text-muted-foreground mt-1">
                {totalProgress}% complete
              </div>
            </div>
            <SectionProgressVertical
              currentSection={progress.currentSection}
              completedSections={progress.completedSections}
              onSectionClick={handleSectionClick}
            />
          </div>
        </aside>
      )}

      {/* Main content */}
      <main className="flex-1 min-w-0">
        {/* Mobile progress */}
        <div className="lg:hidden mb-6 space-y-4">
          <div className="flex items-center gap-4">
            <Progress value={totalProgress} className="flex-1 h-2" />
            <span className="text-sm font-medium">{totalProgress}%</span>
          </div>
          <SectionProgress
            currentSection={progress.currentSection}
            completedSections={progress.completedSections}
            onSectionClick={handleSectionClick}
          />
        </div>

        {/* Question */}
        <AnimatePresence mode="wait">
          {currentQuestion && (
            <QuestionCard
              key={currentQuestion.id}
              question={currentQuestion}
              value={progress.answers[currentQuestion.id]}
              onChange={handleAnswerChange}
              error={error}
              questionNumber={currentVisibleIndex}
              totalQuestions={visibleQuestions.length}
            />
          )}
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t">
          <Button
            variant="ghost"
            onClick={handlePrevious}
            disabled={progress.currentSection === SECTION_ORDER[0] && progress.currentQuestionIndex === 0}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Previous
          </Button>

          <div className="flex items-center gap-2">
            {currentQuestion?.allowSkip !== false && !currentQuestion?.validation?.required && (
              <Button variant="ghost" onClick={handleSkip}>
                Skip
                <SkipForward className="ml-2 h-4 w-4" />
              </Button>
            )}
            
            {/* Check if this is the last question */}
            {progress.currentSection === SECTION_ORDER[SECTION_ORDER.length - 1] &&
             progress.currentQuestionIndex >= (currentSection?.questions.length || 0) - 1 ? (
              <Button onClick={handleComplete}>
                Complete
                <Check className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button onClick={handleNext}>
                Next
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
